import React, { useState, useEffect } from 'react'
import { Button, Space, Table, Tag, Tooltip, DatePicker, Modal, Descriptions } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import type { TableProps, TableColumnsType } from 'antd'
import type { LocationLog } from '../../generated/prisma/browser.js'
import axios from 'axios'
import dayjs from 'dayjs'
import { appApiUrls } from 'src/shared/url-map.js'
import { getProviderFromPath } from 'src/shared/common-utils.js'

const { RangePicker } = DatePicker

// 扩展 LocationLog 类型以包含转换后的字段
type LocationRecord = Omit<LocationLog, 'localizationCode' | 'path' | 'userToken' | 'createdAt' | 'updatedAt'> & {
  userId: string
  language: string
  provider: string
  requestTime: string
  duration: number // 接口耗时（毫秒）
}

type Props = {
  className?: string
}

export default function LocationDetailsTable({ className }: Props) {
  const [data, setData] = useState<LocationRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<LocationRecord | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  })

  const fetchData = async () => {
    setLoading(true)
    try {
      const res = await axios.get(appApiUrls.locationChart, {
        params: { id: 'location-recent-details' },
      })
      if (res.data.code === 0) {
        // 转换后端字段名为前端camelCase格式
        const transformedData = (res.data.data?.rows || []).map((item: any) => {
          // 计算接口耗时（毫秒）
          const createdAt = new Date(item.date)
          const updatedAt = new Date(item.updated_at)
          const duration = updatedAt.getTime() - createdAt.getTime()

          return {
            id: item.id || Math.random().toString(),
            userId: item.user_token || 'EMPTY',
            lat: item.lat?.toString() || '',
            lon: item.lon?.toString() || '',
            language:
              item.localization_code === 'zh-Hans'
                ? '简体中文'
                : item.localization_code === 'en'
                  ? '英文'
                  : item.localization_code === 'zh-Hant'
                    ? '繁体中文'
                    : '其他',
            provider: getProviderFromPath(item.path, item.response),
            responseCode: item.response_code || 0,
            requestTime: item.date || '',
            duration: Math.max(0, duration), // 确保耗时不为负数
            userIp: '',
            query: '',
            response: item.response || '',
            updatedAt: '',
            appInfo: null,
          }
        })
        setData(transformedData)
        setPagination((prev) => ({
          ...prev,
          total: transformedData.length,
        }))
      }
    } catch (error) {
      console.error('Failed to fetch location details:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const columns: TableColumnsType<LocationRecord> = [
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 120,
      filters: Array.from(new Set(data.map((item) => item.userId))).map((userId) => ({
        text: userId === 'EMPTY' ? '匿名用户' : userId.length > 8 ? `${userId.slice(0, 8)}...` : userId,
        value: userId,
      })),
      onFilter: (value: any, record: LocationRecord) => record.userId === value,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {text === 'EMPTY' ? <Tag color="default">匿名</Tag> : text.length > 8 ? `${text.slice(0, 8)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '坐标',
      key: 'coordinates',
      width: 100,
      render: (_: any, record: LocationRecord) => (
        <Tooltip title={`纬度: ${record.lat}, 经度: ${record.lon}`}>
          <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            <div>{parseFloat(record.lat).toFixed(4)}</div>
            <div>{parseFloat(record.lon).toFixed(4)}</div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: '语言',
      dataIndex: 'language',
      key: 'language',
      width: 60,
      filters: [
        { text: '简体中文', value: '简体中文' },
        { text: '英文', value: '英文' },
        { text: '繁体中文', value: '繁体中文' },
        { text: '其他', value: '其他' },
      ],
      onFilter: (value: any, record: LocationRecord) => record.language === value,
      render: (text: string) => {
        const colorMap: Record<string, string> = {
          简体中文: 'blue',
          英文: 'green',
          繁体中文: 'purple',
          其他: 'default',
        }
        return <Tag color={colorMap[text] || 'default'}>{text}</Tag>
      },
    },
    {
      title: '地图供应商',
      dataIndex: 'provider',
      key: 'provider',
      width: 60,
      filters: [
        { text: '高德地图', value: '高德地图' },
        { text: '谷歌地图', value: '谷歌地图' },
      ],
      onFilter: (value: any, record: LocationRecord) => record.provider === value,
      render: (text: string) => {
        const colorMap: Record<string, string> = {
          高德地图: 'red',
          谷歌地图: 'blue',
        }
        return <Tag color={colorMap[text] || 'default'}>{text}</Tag>
      },
    },
    {
      title: '响应状态',
      dataIndex: 'responseCode',
      key: 'responseCode',
      width: 60,
      filters: [
        { text: '成功 (200)', value: 200 },
        { text: '失败 (非200)', value: 'error' },
      ],
      onFilter: (value: any, record: LocationRecord) => {
        if (value === 200) return record.responseCode === 200
        if (value === 'error') return record.responseCode !== 200
        return false
      },
      render: (code: number) => (
        <Tag color={code === 200 ? 'success' : 'error'}>
          {code === 200 ? '成功' : '失败'} ({code})
        </Tag>
      ),
    },
    {
      title: '接口耗时',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      sorter: (a: LocationRecord, b: LocationRecord) => a.duration - b.duration,
      render: (duration: number) => {
        const formatDuration = (ms: number) => {
          if (ms < 1000) {
            return `${ms}ms`
          } else if (ms < 60000) {
            return `${(ms / 1000).toFixed(2)}s`
          } else {
            return `${(ms / 60000).toFixed(2)}min`
          }
        }

        // 根据耗时设置不同的颜色
        const color = duration < 500 ? 'success' : duration < 2000 ? 'warning' : 'error'

        return (
          <Tag color={color} style={{ fontFamily: 'monospace', fontSize: '12px' }}>
            {formatDuration(duration)}
          </Tag>
        )
      },
    },
    {
      title: '请求时间',
      dataIndex: 'requestTime',
      key: 'requestTime',
      width: 160,
      sorter: (a: LocationRecord, b: LocationRecord) => dayjs(a.requestTime).unix() - dayjs(b.requestTime).unix(),
      defaultSortOrder: 'descend',
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <RangePicker
            format="YYYY-MM-DD"
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setSelectedKeys([dates.map((d) => d!.format('YYYY-MM-DD'))])
              } else {
                setSelectedKeys([])
              }
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small">
              确定
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small">
              重置
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value: any, record: LocationRecord) => {
        if (!value || !Array.isArray(value) || value.length !== 2) return true
        const recordTime = dayjs(record.requestTime)
        return recordTime.isAfter(dayjs(value[0])) && recordTime.isBefore(dayjs(value[1]))
      },
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{dayjs(text).format('MM-DD HH:mm:ss')}</span>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_: any, record: LocationRecord) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedRecord(record)
            setDetailModalVisible(true)
          }}
          className="text-blue-500 hover:text-blue-400 dark:text-blue-400 dark:hover:text-blue-300"
        >
          详情
        </Button>
      ),
    },
  ]

  const handleTableChange: TableProps<LocationRecord>['onChange'] = (paginationConfig: any, _filters: any) => {
    if (paginationConfig) {
      setPagination({
        current: paginationConfig.current || 1,
        pageSize: paginationConfig.pageSize || 20,
        total: data.length,
      })
    }
  }

  return (
    <div className={className}>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button type="primary" onClick={fetchData} loading={loading}>
            刷新数据
          </Button>
          <span className="text-gray-600 dark:text-gray-400" style={{ fontSize: '14px' }}>
            共 {data.length} 条记录
          </span>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: 1000, y: 600 }}
        size="small"
        bordered
      />

      <Modal
        title="位置请求详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {selectedRecord && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="用户ID" span={2}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {selectedRecord.userId === 'EMPTY' ? <Tag color="default">匿名用户</Tag> : selectedRecord.userId}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="纬度">
              <span style={{ fontFamily: 'monospace' }}>{selectedRecord.lat}</span>
            </Descriptions.Item>
            <Descriptions.Item label="经度">
              <span style={{ fontFamily: 'monospace' }}>{selectedRecord.lon}</span>
            </Descriptions.Item>
            <Descriptions.Item label="语言">
              <Tag
                color={
                  selectedRecord.language === '简体中文'
                    ? 'blue'
                    : selectedRecord.language === '英文'
                      ? 'green'
                      : selectedRecord.language === '繁体中文'
                        ? 'purple'
                        : 'default'
                }
              >
                {selectedRecord.language}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="地图供应商">
              <Tag
                color={
                  selectedRecord.provider === '高德地图'
                    ? 'red'
                    : selectedRecord.provider === '谷歌地图'
                      ? 'blue'
                      : 'default'
                }
              >
                {selectedRecord.provider}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="响应状态">
              <Tag color={selectedRecord.responseCode === 200 ? 'success' : 'error'}>
                {selectedRecord.responseCode === 200 ? '成功' : '失败'} ({selectedRecord.responseCode})
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="请求时间">
              <span style={{ fontFamily: 'monospace' }}>
                {dayjs(selectedRecord.requestTime).format('YYYY-MM-DD HH:mm:ss')}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="接口耗时">
              <Tag
                color={selectedRecord.duration < 500 ? 'success' : selectedRecord.duration < 2000 ? 'warning' : 'error'}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              >
                {selectedRecord.duration < 1000
                  ? `${selectedRecord.duration}ms`
                  : selectedRecord.duration < 60000
                    ? `${(selectedRecord.duration / 1000).toFixed(2)}s`
                    : `${(selectedRecord.duration / 60000).toFixed(2)}min`}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="API 响应" span={2}>
              <div
                style={{
                  maxHeight: '300px',
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5',
                  padding: '8px',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selectedRecord.response
                  ? (() => {
                      try {
                        return JSON.stringify(JSON.parse(selectedRecord.response), null, 2)
                      } catch {
                        return selectedRecord.response
                      }
                    })()
                  : '无响应数据'}
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  )
}
